import React from "react";

console.log("GrowPractice is rendering..."); // ✅ Debugging log

const GrowPractice = () => {
  const benefits = [
    {
      id: 1,
      stat: "97%",
      title: "Patient Satisfaction",
      description: "Improved patient experience with streamlined scheduling and communication"
    },
    {
      id: 2,
      stat: "45%",
      title: "Time Saved",
      description: "Reduced administrative workload through automation"
    },
    {
      id: 3,
      stat: "2x",
      title: "Growth Rate",
      description: "Doubled practice growth with our integrated solutions"
    }
  ];

  const features = [
    {
      id: 1,
      title: "Patient Engagement",
      description: "Automated appointment reminders, online booking, and patient portal access",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      )
    },
    {
      id: 2,
      title: "Revenue Optimization",
      description: "Smart billing, insurance verification, and revenue cycle management",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      id: 3,
      title: "Practice Analytics",
      description: "Real-time insights and performance metrics to optimize your practice",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      )
    }
  ];

  return (
    <section className="container mx-auto px-8 py-16">
      <div className="flex flex-col md:flex-row items-center justify-between gap-12">
        {/* Left Content */}
        <div className="w-full md:w-1/2 space-y-8">
          <div className="space-y-4">
            <h2 className="text-4xl font-bold text-gray-900">
              See How We Can Help
              <span className="block">Grow Your Practice</span>
            </h2>
            <p className="text-lg text-gray-600">
              Transform your dental practice with our comprehensive suite of tools and watch your business thrive.
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-6">
            {benefits.map((benefit) => (
              <div key={benefit.id} className="text-center p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow">
                <div className="text-3xl font-bold text-orange-500">{benefit.stat}</div>
                <div className="text-sm font-semibold text-gray-900 mt-2">{benefit.title}</div>
                <div className="text-xs text-gray-600 mt-1">{benefit.description}</div>
              </div>
            ))}
          </div>

          {/* Features List */}
          <div className="space-y-6">
            {features.map((feature) => (
              <div key={feature.id} className="flex items-start space-x-4">
                <div className="p-2 bg-orange-100 text-orange-600 rounded-lg">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <div className="pt-4">
            <button className="bg-orange-500 text-white px-8 py-3 rounded-full hover:bg-orange-600 transition-colors text-lg shadow-lg hover:shadow-xl">
              Start Growing Today
            </button>
          </div>
        </div>

        {/* Right Side - Animated Graph */}
        <div className="w-full md:w-1/2 bg-white p-8 rounded-2xl shadow-lg">
          <div className="relative h-[400px]">
            {/* Graph Background */}
            <div className="absolute inset-0 grid grid-cols-4 grid-rows-4">
              {[...Array(16)].map((_, i) => (
                <div key={i} className="border-gray-100 border-b border-l"></div>
              ))}
            </div>
            
            {/* Y-axis labels */}
            <div className="absolute left-0 inset-y-0 w-12 flex flex-col justify-between text-sm text-gray-500">
              <span>100%</span>
              <span>75%</span>
              <span>50%</span>
              <span>25%</span>
              <span>0%</span>
            </div>

            {/* X-axis labels */}
            <div className="absolute bottom-0 left-12 right-0 flex justify-between text-sm text-gray-500">
              <span>Q1</span>
              <span>Q2</span>
              <span>Q3</span>
              <span>Q4</span>
            </div>

            {/* Animated Growth Line */}
            <svg className="absolute inset-0 mt-4" viewBox="0 0 400 300">
              {/* Background gradient */}
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#f97316', stopOpacity: 0.2 }} />
                  <stop offset="100%" style={{ stopColor: '#f97316', stopOpacity: 0 }} />
                </linearGradient>
              </defs>

              {/* Area under the curve */}
              <path
                className="animate-graph-fill"
                d="M 50,250 Q 150,250 200,150 T 350,50 L 350,250 L 50,250"
                fill="url(#gradient)"
              />

              {/* Growth line */}
              <path
                className="animate-graph-draw"
                d="M 50,250 Q 150,250 200,150 T 350,50"
                fill="none"
                stroke="#f97316"
                strokeWidth="3"
              />

              {/* Animated dots */}
              <circle className="animate-graph-dot" cx="50" cy="250" r="4" fill="#f97316" />
              <circle className="animate-graph-dot delay-1000" cx="200" cy="150" r="4" fill="#f97316" />
              <circle className="animate-graph-dot delay-2000" cx="350" cy="50" r="4" fill="#f97316" />
            </svg>

            {/* Growth Indicators */}
            <div className="absolute top-4 right-4 space-y-2">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Growth</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-orange-200 rounded-full"></div>
                <span className="text-sm text-gray-600">Projected</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GrowPractice;
